import { Edge, FamilyTreeNode } from '@/components/diagrams/FlowChart';

import { MarriageGroupData } from '../components/MarriageGroupNode';

type PersonData = {
  id: string;
  isPrimary: boolean;
  [key: string]: unknown;
};

/**
 * Finds all nodes that are reachable from primary clients via BFS traversal.
 * This is shared logic used by both orphaned node filtering and cascading hide.
 *
 * @param nodes - All nodes to consider
 * @param edges - All edges to consider for connectivity
 * @param persons - Person data including isPrimary flag
 * @param nodesToExclude - Set of node IDs to exclude from the graph (treated as if removed)
 * @returns Set of node IDs that are reachable from primary clients
 */
export function findNodesReachableFromPrimaryClients(
  nodes: FamilyTreeNode[],
  edges: Edge[],
  persons: PersonData[],
  nodesToExclude: Set<string> = new Set()
): Set<string> {
  // Find all primary client IDs
  const primaryClientIds = new Set<string>(
    persons.filter((p) => p.isPrimary).map((p) => p.id)
  );

  // Build adjacency list for undirected graph (edges work both ways for connectivity)
  const adjacencyList = new Map<string, Set<string>>();

  edges.forEach((edge) => {
    // Skip hidden edges as they don't represent real connections
    if (edge.style?.opacity === 0) return;

    // Skip edges involving excluded nodes
    if (nodesToExclude.has(edge.source) || nodesToExclude.has(edge.target)) {
      return;
    }

    if (!adjacencyList.has(edge.source)) {
      adjacencyList.set(edge.source, new Set());
    }
    if (!adjacencyList.has(edge.target)) {
      adjacencyList.set(edge.target, new Set());
    }

    adjacencyList.get(edge.source)!.add(edge.target);
    adjacencyList.get(edge.target)!.add(edge.source);
  });

  // Find all nodes that contain primary clients (and aren't excluded)
  const primaryClientNodeIds = new Set<string>();

  nodes.forEach((node) => {
    if (nodesToExclude.has(node.id)) return;

    if (node.type === 'marriageGroup' && node.data) {
      const data = node.data as MarriageGroupData;
      // Check if any spouse or ex-spouse is a primary client
      if (
        primaryClientIds.has(data.spouse1.id) ||
        (data.spouse2 && primaryClientIds.has(data.spouse2.id)) ||
        data.spouse1ExSpouses.some((ex) => primaryClientIds.has(ex.id)) ||
        data.spouse2ExSpouses.some((ex) => primaryClientIds.has(ex.id))
      ) {
        primaryClientNodeIds.add(node.id);
      }
    } else if (node.type === 'person' && node.data?.person) {
      if (primaryClientIds.has(node.data.person.id)) {
        primaryClientNodeIds.add(node.id);
      }
    }
  });

  // If no primary clients found, return empty set
  if (primaryClientNodeIds.size === 0) {
    return new Set<string>();
  }

  // BFS to find all reachable nodes from primary clients
  const reachableNodeIds = new Set<string>();
  const queue = Array.from(primaryClientNodeIds);

  while (queue.length > 0) {
    const nodeId = queue.shift()!;

    if (reachableNodeIds.has(nodeId)) continue;

    reachableNodeIds.add(nodeId);

    const neighbors = adjacencyList.get(nodeId);
    if (neighbors) {
      neighbors.forEach((neighborId) => {
        if (!reachableNodeIds.has(neighborId)) {
          queue.push(neighborId);
        }
      });
    }
  }

  return reachableNodeIds;
}
